import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  Filter,
  Search,
  RefreshCw,
  Package,
  AlertTriangle,
  FileText,
  Users,
  Megaphone,
  Truck,
  Star,
  Clock,
  UserPlus,
  MessageSquare,
  Scale,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { cn } from '../lib/utils';
import { notificationsAPI, Notification, NotificationType, NotificationPriority } from '../lib/api';
import { toast } from 'sonner';

// Notification type icons
const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'po_created':
    case 'po_status_changed':
    case 'po_received':
      return <Package className="h-5 w-5" />;
    case 'po_delivery_approaching':
    case 'po_delivery_overdue':
      return <Truck className="h-5 w-5" />;
    case 'ncr_created':
    case 'ncr_status_changed':
    case 'ncr_assigned':
      return <AlertTriangle className="h-5 w-5" />;
    case 'supplier_rating_changed':
      return <Star className="h-5 w-5" />;
    case 'supplier_qualification_invite':
    case 'supplier_qualification_complete':
      return <FileText className="h-5 w-5" />;
    case 'supplier_query_created':
    case 'supplier_query_responded':
      return <MessageSquare className="h-5 w-5" />;
    case 'rfq_created':
    case 'rfq_response_received':
    case 'rfq_awarded':
      return <FileText className="h-5 w-5" />;
    case 'user_invitation_sent':
    case 'user_invitation_accepted':
    case 'user_mentioned':
      return <Users className="h-5 w-5" />;
    case 'document_uploaded':
    case 'document_expiring':
      return <FileText className="h-5 w-5" />;
    case 'questionnaire_assigned':
      return <FileText className="h-5 w-5" />;
    case 'onboarding_invitation_sent':
    case 'onboarding_invitation_accepted':
    case 'onboarding_submitted':
    case 'onboarding_approved':
    case 'onboarding_declined':
    case 'onboarding_info_requested':
      return <UserPlus className="h-5 w-5" />;
    case 'calloff_sent':
    case 'calloff_confirmed':
    case 'calloff_dispatched':
    case 'calloff_delivery_lines_ready':
    case 'calloff_delivered':
    case 'calloff_cancelled':
    case 'calloff_edited':
      return <Package className="h-5 w-5" />;
    case 'delivery_appeal_submitted':
    case 'delivery_appeal_responded':
      return <Scale className="h-5 w-5" />;
    case 'document_acceptance_required':
      return <FileText className="h-5 w-5" />;
    case 'system_announcement':
    case 'weekly_report':
      return <Megaphone className="h-5 w-5" />;
    default:
      return <Bell className="h-5 w-5" />;
  }
};

// Priority colors
const getPriorityColor = (priority: NotificationPriority) => {
  switch (priority) {
    case 'urgent':
      return 'text-red-600 bg-red-50 dark:bg-red-900/20';
    case 'high':
      return 'text-orange-600 bg-orange-50 dark:bg-orange-900/20';
    case 'normal':
      return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20';
    case 'low':
      return 'text-gray-600 bg-gray-50 dark:bg-gray-800';
    default:
      return 'text-gray-600 bg-gray-50 dark:bg-gray-800';
  }
};

// Notification type categories for filtering
const notificationCategories = [
  { value: 'all', label: 'All Types' },
  { value: 'po', label: 'Purchase Orders' },
  { value: 'ncr', label: 'Quality (NCR)' },
  { value: 'supplier', label: 'Suppliers' },
  { value: 'rfq', label: 'RFQ' },
  { value: 'user', label: 'Users' },
  { value: 'document', label: 'Documents' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'calloff', label: 'Call-Off' },
  { value: 'system', label: 'System' },
];

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterRead, setFilterRead] = useState<string>('all');
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        page,
        page_size: pageSize,
      };

      if (searchQuery) params['search'] = searchQuery;
      if (filterRead === 'unread') params['is_read'] = false;
      if (filterRead === 'read') params['is_read'] = true;
      if (filterType && filterType !== 'all') params['type'] = filterType;

      const response = await notificationsAPI.getNotifications(params);
      setNotifications(response.data.results);
      setTotalCount(response.data.count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [page, filterRead, filterType]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (page === 1) {
        fetchNotifications();
      } else {
        setPage(1);
      }
    }, 300);

    return () => clearTimeout(delaySearch);
  }, [searchQuery]);

  const handleSelectAll = () => {
    if (selectedIds.size === notifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(notifications.map(n => n.id)));
    }
  };

  const handleSelect = (id: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // CR-4756: Notify the TopBar bell so its unread count refreshes immediately
  // instead of waiting for the next 60-second poll. The previous behaviour
  // made the bell count look stale until the polling interval caught up,
  // which users perceived as "self-corrects after a few minutes".
  const notifyBell = () => {
    window.dispatchEvent(new CustomEvent('notifications-updated'));
  };

  const handleMarkAsRead = async (id: number) => {
    // CR-4756: Skip the API call entirely when the notification is already read.
    const target = notifications.find((n) => n.id === id);
    if (target && target.is_read) return;
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, is_read: true } : n))
      );
      toast.success('Notification marked as read');
      notifyBell();
    } catch (error) {
      toast.error('Failed to mark notification as read');
    }
  };

  const handleMarkSelectedAsRead = async () => {
    if (selectedIds.size === 0) return;
    // CR-4756: Only ask the backend to update the unread ones — and base the
    // confirmation toast on what was actually updated, so the user isn't told
    // "20 marked as read" when 18 of them were already read.
    const unreadSelectedIds = Array.from(selectedIds).filter((id) => {
      const n = notifications.find((notif) => notif.id === id);
      return n && !n.is_read;
    });
    if (unreadSelectedIds.length === 0) {
      toast.info('Selected notifications are already read');
      setSelectedIds(new Set());
      return;
    }
    try {
      const response = await notificationsAPI.markSelectedAsRead(unreadSelectedIds);
      const updated = response.data.updated_count ?? unreadSelectedIds.length;
      setNotifications(prev =>
        prev.map(n => (unreadSelectedIds.includes(n.id) ? { ...n, is_read: true } : n))
      );
      setSelectedIds(new Set());
      toast.success(`${updated} notification${updated === 1 ? '' : 's'} marked as read`);
      notifyBell();
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success(response.data.message);
      // CR-3938: Notify TopBar popup to refresh
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    } catch (error) {
      toast.error('Failed to mark all notifications as read');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await notificationsAPI.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setTotalCount(prev => prev - 1);
      toast.success('Notification deleted');
      // CR-3938: Notify TopBar popup to refresh
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const handleDeleteAllRead = async () => {
    try {
      const response = await notificationsAPI.deleteAllRead();
      fetchNotifications();
      toast.success(response.data.message);
      // CR-3938: Notify TopBar popup to refresh
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    } catch (error) {
      toast.error('Failed to delete read notifications');
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6" />
            Notifications
          </h1>
          <p className="text-muted-foreground mt-1">
            {totalCount} total, {unreadCount} unread
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchNotifications}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleMarkAllAsRead}>
                <CheckCheck className="h-4 w-4 mr-2" />
                Mark all as read
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleDeleteAllRead}
                className="text-red-600"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete all read
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterRead} onValueChange={setFilterRead}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unread">Unread</SelectItem>
                <SelectItem value="read">Read</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                {notificationCategories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Selected Actions */}
      {selectedIds.size > 0 && (() => {
        // CR-4756: Disable Mark as read when every selected item is already
        // read — prevents repeat actions that did nothing on the backend.
        const hasUnreadSelected = notifications.some(
          (n) => selectedIds.has(n.id) && !n.is_read,
        );
        return (
        <div className="flex items-center gap-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <span className="text-sm font-medium">
            {selectedIds.size} selected
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={handleMarkSelectedAsRead}
            disabled={!hasUnreadSelected}
            title={hasUnreadSelected ? 'Mark selected as read' : 'All selected notifications are already read'}
          >
            <Check className="h-4 w-4 mr-2" />
            Mark as read
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSelectedIds(new Set())}
          >
            Cancel
          </Button>
        </div>
        );
      })()}

      {/* Notifications List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-4">
            <Checkbox
              checked={selectedIds.size === notifications.length && notifications.length > 0}
              onCheckedChange={handleSelectAll}
            />
            <span className="text-sm text-muted-foreground">Select all</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">
              Loading notifications...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No notifications found</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    'flex items-start gap-4 p-4 hover:bg-muted/50 transition-colors',
                    !notification.is_read && 'bg-blue-50/50 dark:bg-blue-900/10'
                  )}
                >
                  <Checkbox
                    checked={selectedIds.has(notification.id)}
                    onCheckedChange={() => handleSelect(notification.id)}
                  />
                  <div
                    className={cn(
                      'p-2 rounded-full',
                      getPriorityColor(notification.priority)
                    )}
                  >
                    {getNotificationIcon(notification.notification_type)}
                  </div>
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p
                          className={cn(
                            'font-medium',
                            !notification.is_read && 'font-semibold'
                          )}
                        >
                          {notification.title}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {notification.message}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {!notification.is_read && (
                          <Badge variant="default" className="text-xs">
                            New
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-xs capitalize">
                          {notification.priority}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {notification.time_ago}
                      </span>
                      {notification.action_url && (
                        <span className="text-blue-600 dark:text-blue-400">
                          Click to view
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {!notification.is_read && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification.id);
                        }}
                        title="Mark as read"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(notification.id);
                      }}
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * pageSize + 1} to{' '}
            {Math.min(page * pageSize, totalCount)} of {totalCount}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
